/**
 * A higher-order function that wraps a callback with error handling.
 * This is used to prevent errors in event handlers from crashing the application.
 * Works with both synchronous and asynchronous functions.
 *
 * @param callback - The function to wrap with error handling
 * @returns A wrapped version of the callback that catches and logs any errors
 *
 * @example
 * ```ts
 * // Sync usage
 * const handler = withErrorBoundary((event) => {
 *   throw new Error('Something went wrong');
 * });
 *
 * // Async usage
 * const asyncHandler = withErrorBoundary(async (event) => {
 *   throw new Error('Async error');
 * });
 * ```
 */
export function withErrorBoundary<T extends (...args: any[]) => any>(
  callback: T
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  return ((...args: Parameters<T>) => {
    try {
      const result = callback(...args);

      if (result instanceof Promise) {
        return result.catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error in async event handler:', error);
          return undefined;
        });
      }

      return result;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in event handler:', error);
      return undefined;
    }
  }) as any; // Type assertion needed due to TypeScript limitations with conditional types
}
